import React, { useEffect, useRef, useState } from "react";
import { FieldFeature } from "../../../../../../Component/Field/FieldMap";
import { useTranslation } from "react-i18next";
import { normalizeSync } from "normalize-diacritics";
import {
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  TextField,
} from "@mui/material";
import Button from "../../../../../../Component/Button/Button";
import styles from "./FieldSelect.module.css";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate, useParams } from "react-router";
import { ResultSet } from "../../../../../../API/fields";

const MAX_SELECTED_FIELDS = 5;

export type FieldResultMap = {
  [fieldId: number]: ResultSet[];
};

export function filterFields(
  features: FieldFeature[],
  search: string,
  language: string,
): FieldFeature[] {
  return features.filter((feature) => {
    return (
      [
        feature.properties.name,
        feature.properties.crop,
        feature.properties.variety,
      ].filter((value) => {
        if (!value) {
          return false;
        }

        return normalizeSync(value.toLocaleLowerCase(language)).includes(
          normalizeSync(search.toLocaleLowerCase(language)),
        );
      }).length >= 1
    );
  });
}

export type FieldSelectProps = {
  featureList: FieldFeature[];
  onSelectMultipleFields: (selectedFieldIds: number[]) => void;
  selectedFieldIds?: number[];
  maxSelected?: number;
};

export default function FieldSelect({
  featureList,
  onSelectMultipleFields,
  selectedFieldIds = [],
  maxSelected = MAX_SELECTED_FIELDS,
}: FieldSelectProps) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const [popoverAnchor, setPopoverAnchor] = useState<Element | null>(null);
  const [popoverField, setPopoverField] = useState<number | null>(null);
  const navigate = useNavigate();
  const { customerId } = useParams();
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const filteredFeatures = filterFields(featureList, search, i18n.language);

  const firstSelectedId = filteredFeatures.find((feature) =>
    selectedFieldIds.includes(feature.properties.id),
  )?.properties.id;

  useEffect(() => {
    if (!selectedItemRef.current) return;
    selectedItemRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    selectedItemRef.current.focus({ preventScroll: true });
  }, [firstSelectedId]);

  return (
    <>
      <div className={styles.fieldsListBoxSearch}>
        <h1 className={styles.fieldsListTitle}>{t("heading.Fields")}</h1>
        <TextField
          label={t("label.Search fields")}
          fullWidth
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {featureList.length === 0 ? (
        <p className={styles.noFieldsMsg}>{t("text.There are no fields")}</p>
      ) : (
        <List className={styles.fieldList}>
          {filteredFeatures.map((feature) => {
            const isSelected = selectedFieldIds.includes(feature.properties.id);
            const atLimit =
              !isSelected && selectedFieldIds.length >= maxSelected;
            return (
              <ListItem
                key={feature.properties.id}
                disablePadding
                divider
                sx={{
                  backgroundColor: isSelected ? "action.hover" : "transparent",
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    color="secondary"
                    style={{
                      background: "transparent",
                      minWidth: "20px",
                      marginLeft: "7px",
                    }}
                    edge="start"
                    checked={isSelected}
                    disabled={atLimit}
                    tabIndex={-1}
                    onClick={() => {
                      const next = isSelected
                        ? selectedFieldIds.filter(
                            (id) => id !== feature.properties.id,
                          )
                        : [...selectedFieldIds, feature.properties.id];
                      onSelectMultipleFields(next);
                    }}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemButton
                  className={styles.fieldItem}
                  key={feature.properties.id}
                  onClick={() =>
                    onSelectMultipleFields([feature.properties.id])
                  }
                  selected={isSelected}
                  ref={
                    feature.properties.id === firstSelectedId
                      ? selectedItemRef
                      : null
                  }
                >
                  <ListItemText>
                    <strong className={styles.fieldItemTitle}>
                      {feature.properties.name}
                    </strong>
                    <span className={styles.fixedWidthArea}>
                      {t("text.{{area}}.ha", {
                        area: feature.properties.area_ha.toFixed(2),
                      })}
                    </span>
                    <span className={styles.fixedVariety}>
                      {feature.properties.variety}
                    </span>
                  </ListItemText>
                  <IconButton
                    aria-label={t("label.field.actions")}
                    edge="end"
                    onClick={(event) => {
                      setPopoverAnchor(event.currentTarget);
                      setPopoverField(feature.properties.id);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    open={popoverField === feature.properties.id}
                    anchorEl={popoverAnchor}
                    onClose={() => {
                      setPopoverAnchor(null);
                      setPopoverField(null);
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={(ev) => {
                        ev.stopPropagation();
                        navigate(
                          `/customer/${customerId}/field/${feature.properties.id}/edit`,
                        );
                        setPopoverAnchor(null);
                        setPopoverField(null);
                      }}
                    >
                      {t("button.Edit field properties")}
                    </MenuItem>
                    <MenuItem
                      onClick={(ev) => {
                        ev.stopPropagation();
                        navigate(
                          `/customer/${customerId}/field/${feature.properties.id}/edit-extent`,
                        );
                        setPopoverAnchor(null);
                        setPopoverField(null);
                      }}
                    >
                      {t("button.Change field boundaries")}
                    </MenuItem>
                  </Menu>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
      <div className={styles.fieldsListBoxButton}>
        <AddNewFieldButton />
      </div>
    </>
  );
}

const AddNewFieldButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId } = useParams();

  return (
    <Button
      fullWidth
      variant="contained"
      size="large"
      style={{ display: "flex", justifyContent: "space-between" }}
      onClick={() => navigate(`/customer/${customerId}/field/new`)}
    >
      {t("button.Add new field")}
    </Button>
  );
};
